import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { FilterValues } from 'walden-fast/types/filters';
import type UserService from 'walden-fast/services/user';
import type { IntlService } from 'ember-intl';
import { type ClientCompany } from 'walden-fast/types/user';

export default class FilterService extends Service {
  @service('user') declare userService: UserService;
  @service('intl') declare intlService: IntlService;

  @tracked fromMonth: string | undefined;
  @tracked fromYear: string | undefined;
  @tracked toMonth: string | undefined;
  @tracked toYear: string | undefined;
  @tracked clients: ClientCompany[] | undefined;
  @tracked selectedClientIds: string[] = [];
  @tracked dateError = false;
  @tracked isInitialized = false;

  private static readonly STORAGE_KEY = 'global_filters';
  private static readonly ONE_MONTH = 30 * 24 * 60 * 60 * 1000;

  get months() {
    return [
      {
        name: this.intlService.t('components.filterBar.months.january'),
        value: '1',
      },
      {
        name: this.intlService.t('components.filterBar.months.february'),
        value: '2',
      },
      {
        name: this.intlService.t('components.filterBar.months.march'),
        value: '3',
      },
      {
        name: this.intlService.t('components.filterBar.months.april'),
        value: '4',
      },
      {
        name: this.intlService.t('components.filterBar.months.may'),
        value: '5',
      },
      {
        name: this.intlService.t('components.filterBar.months.june'),
        value: '6',
      },
      {
        name: this.intlService.t('components.filterBar.months.july'),
        value: '7',
      },
      {
        name: this.intlService.t('components.filterBar.months.august'),
        value: '8',
      },
      {
        name: this.intlService.t('components.filterBar.months.september'),
        value: '9',
      },
      {
        name: this.intlService.t('components.filterBar.months.october'),
        value: '10',
      },
      {
        name: this.intlService.t('components.filterBar.months.november'),
        value: '11',
      },
      {
        name: this.intlService.t('components.filterBar.months.december'),
        value: '12',
      },
    ];
  }

  get years() {
    const currentYear = new Date().getFullYear();
    const startYear = 2024;
    const years = [];
    for (let year = startYear; year <= currentYear; year++) {
      years.push(year.toString());
    }
    return years;
  }

  get clientCompanies() {
    return this.userService.user ? this.userService.user.clientCompanies : [];
  }

  get isValid() {
    const hasDateFields =
      this.fromMonth || this.fromYear || this.toMonth || this.toYear;
    const completeDateFields =
      this.fromMonth && this.fromYear && this.toMonth && this.toYear;
    const validDates =
      !hasDateFields || (completeDateFields && !this.dateError);
    const hasAnyFilter = this.clients || hasDateFields;
    return hasAnyFilter && validDates;
  }

  get hasAnyFilter() {
    const hasDateFields =
      this.fromMonth || this.fromYear || this.toMonth || this.toYear;
    return this.clients || hasDateFields;
  }

  get currentFilters(): FilterValues | null {
    if (!this.isValid) {
      return null;
    }

    const filters: Partial<FilterValues> = {};

    if (this.fromMonth && this.fromYear && this.toMonth && this.toYear) {
      filters.fromMonth = this.fromMonth;
      filters.fromYear = this.fromYear;
      filters.toMonth = this.toMonth;
      filters.toYear = this.toYear;
    }

    if (this.clients && this.clients.length) {
      filters.clients = this.clients;
    }

    return filters as FilterValues;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    await this.userService.getUser();

    const storedFilters = window.localStorage.getItem(
      FilterService.STORAGE_KEY,
    );
    const now = Date.now();
    const defaultDates = this.getDefaultDateRange();

    let filters: Partial<FilterValues & { lastUpdated?: number }> = {};

    if (storedFilters) {
      try {
        filters = JSON.parse(storedFilters) as Partial<
          FilterValues & { lastUpdated?: number }
        >;

        const hasValidClients =
          Array.isArray(filters.clients) && filters.clients.length > 0;

        if (!hasValidClients) {
          filters.clients = undefined;
        } else {
          const validatedClients = this.validateClientsList(
            filters.clients!,
            this.clientCompanies,
          );
          filters.clients =
            validatedClients.length > 0 ? validatedClients : undefined;
        }

        const hasValidDates =
          filters.fromMonth &&
          filters.fromYear &&
          filters.toMonth &&
          filters.toYear;

        if (!hasValidDates) {
          filters = {
            ...filters,
            ...defaultDates,
            lastUpdated: now,
          };
        } else {
          const isExpired =
            filters.lastUpdated && this.areFiltersExpired(filters.lastUpdated);
          const isLogicallyValid = this.validateDateRangeLogic(
            filters.fromMonth!,
            filters.fromYear!,
            filters.toMonth!,
            filters.toYear!,
          );

          if (isExpired || !isLogicallyValid) {
            filters = {
              ...filters,
              ...defaultDates,
              lastUpdated: now,
            };
          }
        }
      } catch (error) {
        console.error('Error parsing stored filters, using defaults:', error);
        filters = {
          ...defaultDates,
          clients: undefined,
          lastUpdated: now,
        };
      }
    } else {
      filters = {
        ...defaultDates,
        clients: undefined,
        lastUpdated: now,
      };
    }

    this.fromMonth = filters.fromMonth ?? defaultDates.fromMonth;
    this.fromYear = filters.fromYear ?? defaultDates.fromYear;
    this.toMonth = filters.toMonth ?? defaultDates.toMonth;
    this.toYear = filters.toYear ?? defaultDates.toYear;
    this.clients = filters.clients;

    if (this.clients) {
      this.selectedClientIds = this.clients.map((client) => client.id);
    } else {
      this.selectedClientIds = [];
    }

    window.localStorage.setItem(
      FilterService.STORAGE_KEY,
      JSON.stringify(filters),
    );

    this.isInitialized = true;
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
  }

  async getStoredFilters(): Promise<FilterValues | null> {
    await this.ensureInitialized();

    const storedFilters = window.localStorage.getItem(
      FilterService.STORAGE_KEY,
    );
    if (!storedFilters) {
      return null;
    }

    try {
      const filters = JSON.parse(storedFilters) as Partial<
        FilterValues & { lastUpdated?: number }
      >;

      const hasValidDateRange =
        filters.fromMonth &&
        filters.fromYear &&
        filters.toMonth &&
        filters.toYear;
      const hasValidClients = filters.clients && filters.clients.length > 0;

      if (!hasValidDateRange && !hasValidClients) {
        return null;
      }

      let validatedClients: ClientCompany[] | undefined;
      if (filters.clients && filters.clients.length > 0) {
        const availableClientIds = new Set(
          this.clientCompanies.map((c) => c.id),
        );
        validatedClients = filters.clients.filter((client) =>
          availableClientIds.has(client.id),
        );
        if (validatedClients.length === 0) {
          validatedClients = undefined;
        }
      }

      const validatedFilters: FilterValues = {
        fromMonth: hasValidDateRange ? filters.fromMonth! : undefined,
        fromYear: hasValidDateRange ? filters.fromYear! : undefined,
        toMonth: hasValidDateRange ? filters.toMonth! : undefined,
        toYear: hasValidDateRange ? filters.toYear! : undefined,
        clients: validatedClients,
      };

      const stillHasValidFilters =
        (validatedFilters.fromMonth &&
          validatedFilters.fromYear &&
          validatedFilters.toMonth &&
          validatedFilters.toYear) ||
        (validatedFilters.clients && validatedFilters.clients.length > 0);

      if (!stillHasValidFilters) {
        return null;
      }

      return validatedFilters;
    } catch (error) {
      console.error('Error parsing stored filters:', error);
      return null;
    }
  }

  getDefaultDateRange() {
    const toDate = new Date();
    const threeMonthsAgo = new Date(
      toDate.getFullYear(),
      toDate.getMonth() - 2,
      1,
    );
    return {
      fromMonth: (threeMonthsAgo.getMonth() + 1).toString(),
      fromYear: threeMonthsAgo.getFullYear().toString(),
      toMonth: (toDate.getMonth() + 1).toString(),
      toYear: toDate.getFullYear().toString(),
    };
  }

  async loadFilters(): Promise<FilterValues | null> {
    await this.ensureInitialized();
    return this.currentFilters;
  }

  updateFilter(field: string, value: unknown) {
    if (field === 'selectedClientIds') {
      const selectedIds = value as string[] | undefined;

      if (selectedIds && selectedIds.length > 0) {
        this.selectedClientIds = selectedIds;
        this.clients = this.clientCompanies.filter((client) =>
          selectedIds.includes(client.id),
        );
      } else {
        this.selectedClientIds = [];
        this.clients = undefined;
      }
    } else {
      // @ts-expect-error: Dynamic property access
      this[field] = value as string;
    }

    this.validateDateRange();
  }

  updateClientIds(value: unknown) {
    const selectedIds = Array.isArray(value) ? (value as string[]) : [];

    this.selectedClientIds = selectedIds;
    this.clients = selectedIds.length
      ? this.clientCompanies.filter((c) => selectedIds.includes(c.id))
      : undefined;
  }

  validateDateRange() {
    if (this.fromYear && this.fromMonth && this.toYear && this.toMonth) {
      const fromDate = new Date(
        parseInt(this.fromYear),
        parseInt(this.fromMonth) - 1,
        1,
      );
      const toDate = new Date(parseInt(this.toYear), parseInt(this.toMonth), 0);
      if (toDate < fromDate) {
        this.dateError = true;
        return false;
      } else {
        this.dateError = false;
        return true;
      }
    }
    return true;
  }

  applyFilters(): FilterValues | null {
    if (!this.isValid) {
      return null;
    }

    const filters = this.currentFilters;
    if (filters) {
      const filtersWithTimestamp = {
        ...filters,
        lastUpdated: Date.now(),
      };
      window.localStorage.setItem(
        FilterService.STORAGE_KEY,
        JSON.stringify(filtersWithTimestamp),
      );
    }
    return filters;
  }

  clearFilters(): FilterValues {
    this.fromMonth = undefined;
    this.fromYear = undefined;
    this.toMonth = undefined;
    this.toYear = undefined;
    this.clients = undefined;
    this.selectedClientIds = [];
    this.dateError = false;

    const filters: FilterValues = {
      fromMonth: this.fromMonth,
      fromYear: this.fromYear,
      toMonth: this.toMonth,
      toYear: this.toYear,
      clients: this.clients,
    };

    window.localStorage.removeItem(FilterService.STORAGE_KEY);
    return filters;
  }

  clearFiltersForImpersonation() {
    this.isInitialized = false;
    this.clearStoredFilters();
  }

  clearStoredFilters() {
    window.localStorage.removeItem(FilterService.STORAGE_KEY);
  }

  async reset() {
    this.clearStoredFilters();

    this.fromMonth = undefined;
    this.fromYear = undefined;
    this.toMonth = undefined;
    this.toYear = undefined;
    this.clients = undefined;
    this.selectedClientIds = [];
    this.dateError = false;
    this.isInitialized = false;

    await this.initialize();
  }

  getMonthName(monthValue: string): string {
    const monthObj = this.months.find((month) => month.value === monthValue);
    return monthObj?.name || '';
  }

  validateFiltersStructure(filters: Partial<FilterValues>): boolean {
    const hasValidDateRange =
      filters.fromMonth &&
      filters.fromYear &&
      filters.toMonth &&
      filters.toYear;
    const hasValidClients = filters.clients && filters.clients.length > 0;

    return !!(hasValidDateRange || hasValidClients);
  }

  validateDateRangeLogic(
    fromMonth: string,
    fromYear: string,
    toMonth: string,
    toYear: string,
  ): boolean {
    const fromDate = new Date(parseInt(fromYear), parseInt(fromMonth) - 1, 1);
    const toDate = new Date(parseInt(toYear), parseInt(toMonth), 0);

    return toDate >= fromDate;
  }

  validateClientsList(
    clients: ClientCompany[],
    availableClients: ClientCompany[],
  ): ClientCompany[] {
    const availableClientIds = new Set(availableClients.map((c) => c.id));
    return clients.filter((client) => availableClientIds.has(client.id));
  }

  areFiltersExpired(lastUpdated: number): boolean {
    return Date.now() - lastUpdated > FilterService.ONE_MONTH;
  }

  mergeWithDefaults(filters: Partial<FilterValues>): FilterValues {
    const defaultDates = this.getDefaultDateRange();

    return {
      fromMonth: filters.fromMonth || defaultDates.fromMonth,
      fromYear: filters.fromYear || defaultDates.fromYear,
      toMonth: filters.toMonth || defaultDates.toMonth,
      toYear: filters.toYear || defaultDates.toYear,
      clients: filters.clients,
    };
  }

  async initializeForPage(): Promise<FilterValues | null> {
    await this.ensureInitialized();

    const storedFilters = await this.getStoredFilters();
    if (storedFilters) {
      return storedFilters;
    }

    const defaultFilters = this.mergeWithDefaults({});
    return defaultFilters;
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:filter')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('filter') declare altName: FilterService;`.
declare module '@ember/service' {
  interface Registry {
    filter: FilterService;
  }
}
